import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getSido, getSigungu, getDong,
  queryDanji, assignRanker, getDetail, updateDanji,
  syncSearch, loadRankerTargets, saveSync,
} from '../../lib/api/admin-complex-api';
import './ComplexManagement.css';

function useRegion(token, setError) {
  const [sigunguList, setSigunguList] = useState([]);
  const [dongList, setDongList] = useState([]);
  const [sido, setSido] = useState('');
  const [sigungu, setSigungu] = useState('');
  const [dong, setDong] = useState('');

  const onSido = useCallback(async (code) => {
    setSido(code); setSigungu(''); setDong(''); setSigunguList([]); setDongList([]);
    if (!code) return;
    try { setSigunguList(await getSigungu(token, code)); } catch (e) { setError(e?.message); }
  }, [token, setError]);
  const onSigungu = useCallback(async (code) => {
    setSigungu(code); setDong(''); setDongList([]);
    if (!code) return;
    try { setDongList(await getDong(token, code)); } catch (e) { setError(e?.message); }
  }, [token, setError]);

  const bjdCode = dong || sigungu || sido || '';
  return { sigunguList, dongList, sido, sigungu, dong, setDong, onSido, onSigungu, bjdCode };
}

function RegionBar({ sidoList, region }) {
  return (
    <>
      <label>시도</label>
      <select value={region.sido} onChange={(e) => region.onSido(e.target.value)}>
        <option value="">전체</option>
        {sidoList.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
      </select>
      <label>시군구</label>
      <select value={region.sigungu} onChange={(e) => region.onSigungu(e.target.value)} disabled={!region.sido}>
        <option value="">전체</option>
        {region.sigunguList.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
      </select>
      <label>읍면동</label>
      <select value={region.dong} onChange={(e) => region.setDong(e.target.value)} disabled={!region.sigungu}>
        <option value="">전체</option>
        {region.dongList.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
      </select>
    </>
  );
}

function ComplexManagement() {
  const { token } = useAuth();
  const [sidoList, setSidoList] = useState([]);
  const [error, setError] = useState('');
  const [syncOpen, setSyncOpen] = useState(false);

  const region = useRegion(token, setError);
  const [danjiName, setDanjiName] = useState('');
  const [rankerOnly, setRankerOnly] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [detail, setDetail] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [total, setTotal] = useState(0);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    (async () => { try { setSidoList(await getSido(token)); } catch (e) { setError(e?.message || '시도 조회 실패'); } })();
  }, [token]);

  const fetchPage = useCallback(async (targetPage, size = pageSize) => {
    setLoading(true); setSelected(new Set());
    try {
      const d = await queryDanji(token, { bjdCode: region.bjdCode, danjiName, rankerOnly, page: targetPage, pageSize: size });
      setRows(d.items); setTotal(d.total); setPage(d.page);
    } catch (e) { setError(e?.message); setRows([]); setTotal(0); }
    finally { setLoading(false); }
  }, [token, region.bjdCode, danjiName, rankerOnly, pageSize]);

  const doQuery = useCallback(() => fetchPage(1), [fetchPage]);
  const changePageSize = (size) => { setPageSize(size); fetchPage(1, size); };

  const toggle = (code) => setSelected((prev) => {
    const n = new Set(prev); if (n.has(code)) n.delete(code); else n.add(code); return n;
  });

  const doAssign = async () => {
    const codes = [...selected];
    if (codes.length === 0) { alert('선택된 단지가 없습니다.'); return; }
    const { ok, message, count } = await assignRanker(token, codes);
    if (!ok) { alert(message || '랭커 지정 실패'); return; }
    alert(`랭커 지정 완료 (${count}건)`);
    doQuery();
  };

  const doRefresh = async () => {
    const codes = [...selected];
    if (codes.length === 0) { alert('최신화할 단지를 선택하세요.'); return; }
    setLoading(true);
    try {
      const { ok, message, result } = await saveSync(token, { rows: codes.map((c) => ({ complexNo: c })), asRanker: false });
      if (!ok) { alert(message || '최신화 실패'); return; }
      alert(`최신화 완료 — 단지 ${result?.savedDanji || 0} / 평형 ${result?.savedArea || 0}`);
      doQuery();
    } catch (e) { alert(e?.message || '최신화 실패'); }
    finally { setLoading(false); }
  };

  const openDetail = async (code) => {
    const { ok, message, detail: d } = await getDetail(token, code);
    if (!ok) { alert(message || '상세 조회 실패'); return; }
    setDetail(d);
  };

  return (
    <div className="complex-management">
      {!!error && <div className="admin-table-status error">{error}</div>}

      <div className="cx-notice">
        <strong>랭커단지란?</strong> 스크래핑 시 대상이 되는 단지 &amp; 평형 조합입니다.
      </div>

      <div className="cx-bar">
        <RegionBar sidoList={sidoList} region={region} />
        <input className="cx-search" value={danjiName} onChange={(e) => setDanjiName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') doQuery(); }} placeholder="단지명" />
        <label className="cx-check"><input type="checkbox" checked={rankerOnly} onChange={(e) => setRankerOnly(e.target.checked)} /> 랭커단지만</label>
        <button className="cx-btn primary" onClick={doQuery}>조회</button>
        <span className="cx-bar-spacer" />
        <button className="cx-btn" onClick={doRefresh}>단지정보 최신화</button>
        <button className="cx-btn" onClick={doAssign}>랭커 단지로 추가</button>
        <button className="cx-btn accent" onClick={() => setSyncOpen(true)}>+ 네이버에서 단지 추가</button>
      </div>

      <div className="cx-hint">행을 더블클릭하면 단지 상세가 열립니다. 네이버에서 새 단지를 수집하려면 우측 상단 「+ 네이버에서 단지 추가」.</div>

      <div className="cx-paging">
        <select className="cx-page-size" value={pageSize} onChange={(e) => changePageSize(Number(e.target.value))}>
          <option value={30}>30개씩</option>
          <option value={50}>50개씩</option>
          <option value={100}>100개씩</option>
        </select>
        <button className="cx-btn sm" disabled={page <= 1 || loading} onClick={() => fetchPage(1)}>«</button>
        <button className="cx-btn sm" disabled={page <= 1 || loading} onClick={() => fetchPage(page - 1)}>이전</button>
        <span className="cx-paging-info">{page} / {totalPages} 페이지 · 총 {total.toLocaleString('ko-KR')}건</span>
        <button className="cx-btn sm" disabled={page >= totalPages || loading} onClick={() => fetchPage(page + 1)}>다음</button>
        <button className="cx-btn sm" disabled={page >= totalPages || loading} onClick={() => fetchPage(totalPages)}>»</button>
      </div>

      {loading && <div className="admin-table-status">처리 중...</div>}
      <div className="cx-table-wrap">
        <table className="admin-basic-table cx-table">
          <thead>
            <tr>
              <th style={{ width: 34 }} />
              <th>구분</th><th>단지코드</th><th>지역</th><th>시군구</th><th>읍면동</th><th>단지명</th>
              <th>유형</th><th>사용승인</th><th className="num">세대수</th><th className="num">임대</th><th className="num">평세대</th>
              <th>평형</th><th>구조</th><th className="num">최고층</th><th>지하철</th><th>랭커</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.danjiCode} className={r.isRanker ? 'ranker' : ''}
                onDoubleClick={() => openDetail(r.danjiCode)} title="더블클릭: 상세">
                <td onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.has(r.danjiCode)} onChange={() => toggle(r.danjiCode)} />
                </td>
                <td>{r.gubun || '-'}</td><td className="muted">{r.danjiCode}</td><td>{r.region}</td>
                <td>{r.sigungu}</td><td>{r.dong}</td><td>{r.buildingName}</td><td>{r.hscpType}</td>
                <td>{r.approveDate}</td><td className="num">{r.familyCnt}</td><td className="num">{r.leaseHouseholds}</td>
                <td className="num">{r.pyeongHouseholds}</td><td>{r.planTypeName}</td><td>{r.structure}</td>
                <td className="num">{r.maxFloor}</td><td>{r.subwayInfo}</td>
                <td>{r.isRanker ? 'Y' : ''}</td>
              </tr>
            ))}
            {rows.length === 0 && !loading && <tr><td colSpan={17} className="muted center">조회 결과가 없습니다.</td></tr>}
          </tbody>
        </table>
      </div>

      {detail && (
        <DetailModal token={token} detail={detail}
          onClose={() => setDetail(null)}
          onSaved={() => { setDetail(null); fetchPage(page); }} />
      )}
      {syncOpen && (
        <SyncModal token={token} sidoList={sidoList} setError={setError}
          onClose={() => setSyncOpen(false)}
          onSaved={() => doQuery()} />
      )}
    </div>
  );
}

// ===== 동기화 모달 (네이버 단지 수집) =====
function SyncModal({ token, sidoList, setError, onClose, onSaved }) {
  const region = useRegion(token, setError);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  const toggle = (no) => setSelected((prev) => { const n = new Set(prev); if (n.has(no)) n.delete(no); else n.add(no); return n; });
  const allChecked = rows.length > 0 && selected.size === rows.length;
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(rows.map((r) => r.complexNo)));

  const doSearch = async () => {
    setLoading(true); setSelected(new Set());
    const { ok, message, rows: r } = await syncSearch(token, { sido: region.sido, sigungu: region.sigungu, dong: region.dong });
    setLoading(false);
    if (!ok) { setError(message || '검색 실패'); setRows([]); return; }
    setRows(r);
  };
  const doLoadTargets = async () => {
    setLoading(true); setSelected(new Set());
    const { ok, message, rows: r } = await loadRankerTargets(token, region.bjdCode);
    setLoading(false);
    if (!ok) { setError(message); setRows([]); return; }
    setRows(r);
  };
  const doSave = async (asRanker) => {
    const picked = rows.filter((r) => selected.has(r.complexNo));
    if (picked.length === 0) { alert('선택된 단지가 없습니다.'); return; }
    setLoading(true);
    try {
      const { ok, message, result } = await saveSync(token, {
        rows: picked.map((r) => ({ complexNo: r.complexNo, realEstateTypeCode: r.realEstateTypeCode })),
        asRanker,
      });
      if (!ok) { alert(message || '저장 실패'); return; }
      alert(`저장 완료 — 기존 ${result?.existingCount || 0} / 단지 ${result?.savedDanji || 0} / 평형 ${result?.savedArea || 0}${asRanker ? ` / 랭커 ${result?.savedRanker || 0}` : ''}`);
      onSaved?.();
    } catch (e) { alert(e?.message || '저장 실패'); }
    finally { setLoading(false); }
  };

  return (
    <div className="cx-modal-bg" onClick={onClose}>
      <div className="cx-sync-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cx-sync-head">
          <div>
            <div className="cx-modal-title">네이버에서 단지 추가</div>
            <div className="cx-sync-sub">지역을 선택해 단지목록을 검색하고, 저장할 단지를 선택하세요.</div>
          </div>
          <button className="cx-modal-x" onClick={onClose} aria-label="닫기">×</button>
        </div>

        <div className="cx-sync-toolbar">
          <div className="cx-bar cx-bar-region">
            <RegionBar sidoList={sidoList} region={region} />
          </div>
          <div className="cx-sync-actions-top">
            <button className="cx-btn primary" onClick={doSearch}>검색(네이버)</button>
            <button className="cx-btn" onClick={doLoadTargets}>랭커대상단지 불러오기</button>
          </div>
        </div>
        {loading && <div className="cx-sync-loading">처리 중… 네이버 크롤은 다소 시간이 걸립니다.</div>}

        <div className="cx-table-wrap sync-modal">
          <table className="admin-basic-table cx-table">
            <thead>
              <tr>
                <th style={{ width: 34 }}><input type="checkbox" checked={allChecked} onChange={toggleAll} /></th>
                <th>단지코드</th><th>단지명</th><th>유형</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.complexNo}>
                  <td><input type="checkbox" checked={selected.has(r.complexNo)} onChange={() => toggle(r.complexNo)} /></td>
                  <td className="muted">{r.complexNo}</td><td>{r.complexName}</td><td>{r.realEstateTypeName}</td>
                </tr>
              ))}
              {rows.length === 0 && !loading && <tr><td colSpan={4} className="muted center">지역을 선택하고 검색하거나, 랭커대상단지를 불러오세요.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="cx-modal-actions">
          <span className="cx-selected-count">{selected.size}개 선택됨</span>
          <button className="cx-btn" onClick={onClose}>닫기</button>
          <button className="cx-btn" onClick={() => doSave(false)}>선택 DB 저장</button>
          <button className="cx-btn primary" onClick={() => doSave(true)}>선택 저장 + 랭커지정</button>
        </div>
      </div>
    </div>
  );
}

const HSCP_OPTIONS = [
  ['', '(미지정)'], ['SYCA101', '아파트'], ['SYCA102', '오피스텔'], ['SYCA103', '주상복합'],
  ['SYCA104', '재건축'], ['SYCA105', '아파트 분양권'], ['SYCA106', '오피스텔 분양권'],
  ['SYCA107', '주상복합 분양권'], ['SYCA118', '빌라/연립'], ['SYCA120', '재개발'],
];
const numFmt = (v) => (v === null || v === undefined || v === '' ? '-' : Number(v).toLocaleString('ko-KR'));

function DetailModal({ token, detail, onClose, onSaved }) {
  const [form, setForm] = useState({
    buildingName: detail.buildingName ?? '',
    bjdCode: detail.bjdCode ?? '',
    addressBunji: detail.addressBunji ?? '',
    familyCnt: detail.familyCnt ?? '',
    approveDate: detail.approveDate ?? '',
    maxFloor: detail.maxFloor ?? '',
    minFloor: detail.minFloor ?? '',
    totBldCnt: detail.totBldCnt ?? '',
    floorAreaRatio: detail.floorAreaRatio ?? '',
    buildingCoverageRatio: detail.buildingCoverageRatio ?? '',
    totalParkingCount: detail.totalParkingCount ?? '',
    parkingCountPerHousehold: detail.parkingCountPerHousehold ?? '',
    constructionCompany: detail.constructionCompany ?? '',
    isService: !!detail.isService,
    hscpTypeCode: detail.hscpTypeCode ?? '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toNum = (v) => (v === '' || v === null ? null : Number(v));

  const areas = detail.areas || [];

  const save = async () => {
    setSaving(true);
    try {
      const { ok, message } = await updateDanji(token, {
        naverDanjiCode: detail.naverDanjiCode,
        buildingName: form.buildingName,
        bjdCode: form.bjdCode,
        addressBunji: form.addressBunji,
        familyCnt: toNum(form.familyCnt),
        approveDate: form.approveDate,
        maxFloor: toNum(form.maxFloor),
        minFloor: toNum(form.minFloor),
        totBldCnt: toNum(form.totBldCnt),
        floorAreaRatio: toNum(form.floorAreaRatio),
        buildingCoverageRatio: toNum(form.buildingCoverageRatio),
        totalParkingCount: toNum(form.totalParkingCount),
        parkingCountPerHousehold: toNum(form.parkingCountPerHousehold),
        constructionCompany: form.constructionCompany,
        isService: form.isService,
        hscpTypeCode: form.hscpTypeCode,
      });
      if (!ok) { alert(message || '저장 실패'); return; }
      alert('저장되었습니다.');
      onSaved?.();
    } catch (e) { alert(e?.message || '저장 실패'); }
    finally { setSaving(false); }
  };

  const field = (label, k, type = 'text') => (
    <label className="cx-field" key={k}>
      <span>{label}</span>
      <input type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} />
    </label>
  );

  return (
    <div className="cx-modal-bg" onClick={onClose}>
      <div className="cx-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cx-sync-head">
          <div>
            <div className="cx-modal-title">단지 상세 / 수정</div>
            <div className="cx-sync-sub">
              {detail.naverDanjiCode} · {[detail.sidoName, detail.sigunguName, detail.bjdName].filter(Boolean).join(' ') || '-'}
              {detail.gubun ? ` · 랭커: ${detail.gubun}` : ''}
            </div>
          </div>
          <button className="cx-modal-x" onClick={onClose} aria-label="닫기">×</button>
        </div>

        <div className="cx-detail-body">
          <div className="cx-form-grid">
            {field('단지명', 'buildingName')}
            {field('법정동코드(bjd_code)', 'bjdCode')}
            {field('지번주소', 'addressBunji')}
            {field('사용승인일', 'approveDate')}
            {field('세대수', 'familyCnt', 'number')}
            {field('최고층', 'maxFloor', 'number')}
            {field('최저층', 'minFloor', 'number')}
            {field('동수', 'totBldCnt', 'number')}
            {field('용적률', 'floorAreaRatio', 'number')}
            {field('건폐율', 'buildingCoverageRatio', 'number')}
            {field('총주차대수', 'totalParkingCount', 'number')}
            {field('세대당주차', 'parkingCountPerHousehold', 'number')}
            {field('시공사', 'constructionCompany')}
            <label className="cx-field">
              <span>유형</span>
              <select value={form.hscpTypeCode} onChange={(e) => set('hscpTypeCode', e.target.value)}>
                {HSCP_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label className="cx-field cx-field-check">
              <span>노출(is_service)</span>
              <input type="checkbox" checked={form.isService} onChange={(e) => set('isService', e.target.checked)} />
            </label>
          </div>

          <div className="cx-area-title">평형 정보 (danji_area) — <em>분홍색</em>은 랭커 설정된 평형</div>
          <div className="cx-area-wrap">
            <table className="admin-basic-table cx-table cx-area-table">
              <thead>
                <tr>
                  <th>ptp_no</th><th>평형명</th><th>평형명2</th>
                  <th className="num">공급면적</th><th className="num">전용면적</th><th className="num">평형세대수</th>
                  <th className="num">재산세</th><th className="num">종부세</th><th>랭커</th>
                </tr>
              </thead>
              <tbody>
                {areas.map((a, i) => (
                  <tr key={`${a.ptpNo}-${i}`} className={a.isRanker ? 'ranker' : ''}>
                    <td>{a.ptpNo || '-'}</td><td>{a.planTypeName || '-'}</td><td>{a.planTypeName2 || '-'}</td>
                    <td className="num">{numFmt(a.splySpc)}</td><td className="num">{numFmt(a.exclsSpc)}</td>
                    <td className="num">{numFmt(a.familyCnt)}</td>
                    <td className="num">{numFmt(a.propertyTotalTax)}</td><td className="num">{numFmt(a.realEstateTotalTax)}</td>
                    <td>{a.isRanker ? 'Y' : ''}</td>
                  </tr>
                ))}
                {areas.length === 0 && <tr><td colSpan={9} className="muted center">평형 정보가 없습니다.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="cx-modal-actions">
          <button className="cx-btn" onClick={onClose}>닫기</button>
          <button className="cx-btn primary" onClick={save} disabled={saving}>{saving ? '저장 중…' : '저장'}</button>
        </div>
      </div>
    </div>
  );
}

export default ComplexManagement;
